/**
 * 관절이 갈 수 있는 곳 — <b>모형이 말한 한계</b>(SR-SIM-09).
 *
 * <p>모터는 얼마든지 돌지만 모형은 아니다. 팔은 어느 각도에서 제 몸에 부딪히고, 집게는 다 닫히면
 * 더 닫히지 않는다. 그래서 한계가 필요하다 — 다만 <b>그 값은 모형이 말해 주어야 한다</b>.
 *
 * <p><b>한때 `swing` 에서 어림했다가 두 번 틀렸다</b>(2026-09-18). 그 값의 뜻은 '어디서 막히나' 가
 * 아니라 '얼마나 움직이게 그렸나' 여서, ① 한 바퀴 도는 회전판이 반 바퀴에서 섰고(로봇팔 받침),
 * ② 한쪽으로만 그린 관절 15개가 <b>반대로 돌리면 아무 일도 일어나지 않았다</b>(불도저 날·집게차 팔).
 * 둘 다 아이 눈에는 그냥 <b>아무 일도 안 일어나는 것</b>으로 보인다 — 움직임도 메시지도 없다.
 * 부품이 겹쳐 보이는 것보다 그 조용함이 수업에서 더 나쁘다.
 *
 * <p>그래서 <b>추측하지 않는다</b>. 관절이 `limit` 를 적어 두었을 때만 그만큼만 간다.
 * 그 값은 굽는 쪽(Campus)이 <b>모양에서 잰 것</b>이다 — 43종 88포트 중 38곳에 적혀 있다
 * (2026-09-19 · Campus `joints/LIMITS.md`). 적지 않은 관절은 자르지 않는다.
 *
 * <p>그 위에 <b>관리자가 덮어쓸 수 있다</b>(Campus 어드민 '3D 콘텐츠' · V169). 모형 파일은 다시 굽기 전에는
 * 못 고치므로, 관리자가 정한 값은 따로 받아 포트의 모터 관절에 얹는다({@link overridden}). 선생님·학생 화면은
 * 적용만 한다 — 고치는 곳은 어드민 하나다.
 *
 * <p>여기 있는 것은 <b>산수뿐이다</b>: three 도 장면도 모른다.
 */

/** 갈 수 있는 각도 구간(도). 제자리는 언제나 0 이다 */
export type Limit = { min: number; max: number };

/** 한계를 셈할 때 필요한 것만 — 노드도 축도 여기서는 쓰지 않는다 */
export type LimitSpec = {
  id: string;
  /** `이 관절 = follow.joint × ratio` */
  follow: { joint: string; ratio: number } | null;
  /** 이 관절이 스스로 아는 한계. 없으면 사슬에서 얻어 온다 */
  limit: Limit | null;
};

/**
 * 모형이 <b>적어 둔</b> 한계를 읽는다 — 도는 관절은 도, 미끄러지는 관절은 cm.
 *
 * <p>적지 않았으면 <b>없는 것이다</b>(null). 없는 것을 다른 값에서 지어내지 않는다 —
 * `swing`·`travel` 은 애니메이션을 그린 범위일 뿐, 어디서 막히는지가 아니다(위 설명).
 */
export function statedLimit(j: { limit?: unknown } | null | undefined): Limit | null {
  const l = j?.limit as { min?: number; max?: number } | undefined;
  if (!l || !Number.isFinite(l.min) || !Number.isFinite(l.max)) return null;
  const min = Math.min(l.min as number, l.max as number), max = Math.max(l.min as number, l.max as number);
  return min === max ? null : { min, max };            // 폭이 0 이면 못 움직인다는 뜻이 아니라 잘못 적은 것이다
}

/**
 * <b>한계도 맞물려 옮겨 간다.</b>
 *
 * <p>어깨가 ±72° 까지만 굽는다면, 그 어깨를 3:1 로 미는 피니언은 ±216° 까지만 돌 수 있다 —
 * 기어는 한쪽만 멈추지 않는다. 그래서 관절 하나가 아는 한계를 맞물린 사슬 전체로 퍼뜨리고,
 * 여러 값이 겹치면 <b>가장 좁은 것</b>을 남긴다: 먼저 걸리는 쪽이 먼저 멈춘다.
 *
 * <p>이렇게 해 두면 나중에 값을 자를 때 사슬이 어긋나지 않는다 — 한계 자체가 이미 비율에 맞아 있어,
 * 어느 관절에서 잘라도 맞물린 상대가 같은 자리에서 멈춘다.
 *
 * @returns 한계가 있는 관절만 담은 표. 없는 관절(바퀴 등)은 아예 들어 있지 않다.
 */
export function spreadLimits(specs: Iterable<LimitSpec>): Map<string, Limit> {
  const all = new Map<string, LimitSpec>();
  for (const s of specs) all.set(s.id, s);

  // 나를 따르는 관절들 — 사슬은 양쪽으로 퍼진다(모터가 어느 쪽에 붙었는지는 모형마다 다르다)
  const followers = new Map<string, LimitSpec[]>();
  for (const s of all.values()) {
    if (!s.follow || !all.has(s.follow.joint)) continue;
    const list = followers.get(s.follow.joint) ?? [];
    list.push(s);
    followers.set(s.follow.joint, list);
  }

  const out = new Map<string, Limit>();
  const tighten = (id: string, lo: number, hi: number) => {
    const cur = out.get(id);
    // `+ 0` 은 -0 을 0 으로 되돌린다: 음수 비율을 곱하면 0 이 -0 이 되고, 값은 같은데 눈으로 읽을 때만 다르다
    out.set(id, { min: (cur ? Math.max(cur.min, lo) : lo) + 0, max: (cur ? Math.min(cur.max, hi) : hi) + 0 });
  };

  for (const src of all.values()) {
    if (!src.limit) continue;
    const { min, max } = src.limit;
    tighten(src.id, min, max);
    // 배율 k 를 들고 퍼뜨린다 — 다른 관절의 각도 = k × src 의 각도
    const seen = new Set<string>([src.id]);
    const queue: { id: string; k: number }[] = [{ id: src.id, k: 1 }];
    while (queue.length) {
      const { id, k } = queue.shift() as { id: string; k: number };
      const step = (to: string, k2: number) => {
        if (seen.has(to) || !Number.isFinite(k2) || k2 === 0) return;
        seen.add(to);
        const a = min * k2, b = max * k2;
        tighten(to, Math.min(a, b), Math.max(a, b));     // 비율이 음수면 위아래가 뒤집힌다
        queue.push({ id: to, k: k2 });
      };
      const me = all.get(id) as LimitSpec;
      if (me.follow && all.has(me.follow.joint)) step(me.follow.joint, k / me.follow.ratio);
      for (const f of followers.get(id) ?? []) step(f.id, k * (f.follow as { ratio: number }).ratio);
    }
  }
  return out;
}

/** 갈 수 있는 데까지만 — 넘겨 주면 가장 가까운 끝값에 세운다 */
export function hold(deg: number, limit: Limit | null | undefined): number {
  if (!limit) return deg;
  return Math.min(Math.max(deg, limit.min), limit.max);
}

/**
 * 관리자가 정한 포트 한계(Campus `GET /v1/public/3d/models/{slug}/motor-limits`).
 * <p>값은 <b>포트 모터의 각(도)</b>이다. `unlimited` 면 구운 한계도 없앤다(계속 돈다).
 */
export type LimitOverride = { unlimited: boolean; min: number | null; max: number | null };

const PORT_RE = /^[A-F]$/;

/**
 * 받은 표를 <b>믿지 않고</b> 읽는다 — 틀린 줄은 버리고 나머지만 쓴다.
 * <p>제자리(0)가 범위 밖인 값은 버린다: 그러면 모형이 처음부터 막힌 채 떠서 아이는 빨간 포트만 본다.
 * 서버가 이미 막지만(V169 제약), 여기가 마지막 문이다 — 한 줄이 틀렸다고 모형 전체를 못 쓰게 두지 않는다.
 */
export function parseOverrides(body: unknown): Record<string, LimitOverride> {
  const out: Record<string, LimitOverride> = {};
  if (!body || typeof body !== "object") return out;
  for (const [port, v] of Object.entries(body as Record<string, unknown>)) {
    if (!PORT_RE.test(port) || !v || typeof v !== "object") continue;
    const o = v as { unlimited?: unknown; min?: unknown; max?: unknown };
    if (o.unlimited === true) { out[port] = { unlimited: true, min: null, max: null }; continue; }
    const min = o.min, max = o.max;
    if (typeof min !== "number" || typeof max !== "number" || !Number.isFinite(min) || !Number.isFinite(max)) continue;
    if (min > 0 || max < 0 || min >= max) continue;
    out[port] = { unlimited: false, min, max };
  }
  return out;
}

/** 모터 관절의 한계 — 관리자 값이 있으면 그것이, 없으면 모형이 적어 둔 값이 이긴다 */
export function overridden(stated: Limit | null, o: LimitOverride | undefined): Limit | null {
  if (!o) return stated;
  if (o.unlimited) return null;
  return { min: o.min as number, max: o.max as number };
}
